<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { linkIsRouteLocation } from '@/composables/useToasts';
import type { Toast, ToastType } from '@/types/toasts';

const props = defineProps<{
    toast: Toast;
}>();

const emit = defineEmits<{
    close: [uuid: string];
}>();

const { t } = useI18n();

const computedIcon = computed<string>(() => {
    const iconMap = new Map<ToastType, string>([
        ['error', 'do_not_disturb_alt'],
        ['info', 'info'],
        ['warning', 'error_outline'],
        ['success', 'check_circle'],
    ]);
    return iconMap.get(props.toast.type) ?? 'info';
});

function close() {
    emit('close', props.toast.uuid);
}

const toastAttrs = computed(() => {
    if (!props.toast.link || linkIsRouteLocation(props.toast.link)) return;
    return {
        tag: 'a',
        href: props.toast.link,
        target: '_blank',
        rel: 'noopener noreferrer',
    };
});

const router = useRouter();

const onToastClick = () => {
    if (!props.toast.link) close();
    if (!linkIsRouteLocation(props.toast.link)) return;
    router.push(props.toast.link);
    close();
};
</script>

<template>
    <VSheet
        v-bind="toastAttrs"
        class="pa-4 text-decoration-none d-flex ga-2 no-wrap"
        :class="{ 'cursor-pointer': toast.link }"
        elevation="6"
        width="25rem"
        :tabindex="toast.link ? 0 : -1"
        rounded
        data-testid="elementCommonToast"
        @click="onToastClick"
    >
        <div
            class="flex-0-0"
            :style="{ lineHeight: 1 }"
        >
            <VIcon
                :color="props.toast.type"
                :icon="computedIcon"
            />
        </div>
        <div class="flex-1-1 d-flex flex-column ga-1">
            <div
                class="text-body-1 font-weight-semibold"
                data-testid="elementCommonToastTitle"
            >
                {{ toast.title }}
            </div>
            <div
                v-if="toast.description"
                class="text-body-2 font-weight-regular text-medium-emphasis pb-1"
                data-testid="elementCommonToastDescription"
            >
                {{ toast.description }}
            </div>
        </div>
        <div class="flex-0-0">
            <VBtn
                :aria-label="t('close')"
                class="text-medium-emphasis"
                variant="text"
                size="24"
                icon
                data-testid="buttonCommonToastClose"
                @click.stop="close"
            >
                <VIcon icon="close" />
            </VBtn>
        </div>
    </VSheet>
</template>
